import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, RouterModule, Router, NavigationEnd, RouterLink, RouterLinkActive } from '@angular/router';
import { environment } from '../environments/environment';
import { filter } from 'rxjs/operators';
import { SeoService } from './services/seo.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterModule, RouterLink, RouterLinkActive],
  templateUrl: './app.html',
  styleUrls: ['./app.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  isLoading = false;
  isLoggedIn = false;
  userName = '';
  userCredits = 0;
  mobileMenuOpen = false;
  userMenuOpen = false;

  private updateInterval: any;
  private userMenuTimeout: any;

  constructor(private router: Router, private seoService: SeoService) { }

  ngOnInit() {
    this.seoService.init();

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      window.scrollTo(0, 0);
      this.closeMobileMenu();
      this.userMenuOpen = false;
    });

    this.checkAuthStatus();

    window.addEventListener('storage', () => {
      this.checkAuthStatus();
    });

    this.startRealTimeUpdates();
  }

  ngOnDestroy() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
    if (this.userMenuTimeout) {
      clearTimeout(this.userMenuTimeout);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.user-menu')) {
      this.userMenuOpen = false;
    }
  }

  private checkAuthStatus() {
    const token = localStorage.getItem('auth_token');
    const user = localStorage.getItem('userData');

    this.isLoggedIn = !!(token && user);

    if (this.isLoggedIn && user) {
      try {
        const userData = JSON.parse(user);
        this.userName = userData.fullName || 'User';
        this.userCredits = userData.credits || 0;
        if (token) {
          this.updateUserData();
        }
      } catch (error) {
        console.warn('Failed to parse user data, logging out');
        this.logout();
      }
    } else {
      this.userName = '';
      this.userCredits = 0;
    }

    window.addEventListener('userDataUpdated', (event: any) => {
      const updatedUser = event.detail;
      this.userName = updatedUser.fullName || 'User';
      this.userCredits = updatedUser.credits || 0;
    });
  }

  logout() {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('userData');
    this.checkAuthStatus();
    this.router.navigate(['/login']);
  }

  private startRealTimeUpdates() {
    this.updateInterval = setInterval(() => {
      if (this.isLoggedIn) {
        this.updateUserData();
      }
    }, 30000);
  }

  private updateUserData() {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    fetch(`${environment.apiUrl}/user/links-simple.php?limit=1`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (response.status === 401) {
          this.logout();
          return;
        }
        return response.json();
      })
      .then(data => {
        if (data && data.success && data.data && data.data.user) {
          this.userCredits = data.data.user.credits;
          try {
            const currentUser = JSON.parse(localStorage.getItem('userData') || '{}');
            const updatedUser = { ...currentUser, ...data.data.user };
            localStorage.setItem('userData', JSON.stringify(updatedUser));
          } catch (error) {
            console.warn('Failed to update user data in localStorage');
          }
        } else if (data && data.logout) {
          this.logout();
        }
      })
      .catch(() => { /* Silent error handling */ });
  }

  toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    document.body.style.overflow = this.mobileMenuOpen ? 'hidden' : '';
  }

  closeMobileMenu() {
    this.mobileMenuOpen = false;
    document.body.style.overflow = '';
  }

  toggleUserMenu(event?: MouseEvent) {
    event?.stopPropagation();
    this.userMenuOpen = !this.userMenuOpen;
  }

  get userInitials(): string {
    if (!this.userName) return 'U';
    const parts = this.userName.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return this.userName[0].toUpperCase();
  }
}
